import { useState } from "react";
import { useOutletContext, useParams } from "react-router";
import styled from "styled-components";

import AssignedBimClasses from "./AssignedBimClasses";
import CategoryEditor from "./CategoryEditor";
import CategoryTitle from "./CategoryTitle";
import ParentCategory from "./ParentCategory";
import * as UI from "../../../../../modules/ui-kit";
import { useIsDirty } from "../../../modules/unsaved-changes";

import type { CategoryOutletContext } from "../WithCategoryInventory";

const StyledCategoryDetail = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    height: 100%;
    overflow-y: auto;
    padding-right: 4px;

    .actions {
        border-top: 1px solid #ebecf0;
        display: flex;
        flex-shrink: 0;
        justify-content: flex-end;
        padding: 16px 0 8px;
    }

    /*
     * Direct children only: @monaco-editor/react wraps the editor in its own
     * <section>, and a bare descendant selector turned that into a column flex
     * container, which stopped the editor from stretching to its parent.
     */
    > section {
        display: flex;
        flex-direction: column;
        /* This column scrolls — sections keep their content height instead of
         * being squeezed. */
        flex-shrink: 0;
        gap: 12px;

        h3 {
            color: #172b4d;
            font-size: 0.95rem;
            font-weight: 600;
            letter-spacing: 0.02em;
            margin: 0;
            padding-bottom: 6px;
            border-bottom: 1px solid #ebecf0;
            text-transform: uppercase;
        }
    }
`;

export default function CategoryDetail() {
    const { categoryID } = useParams();

    const guard = useOutletContext<CategoryOutletContext>();

    // Basics and Assigned BimClasses are both registered editors, so one
    // Reset/Save pair at the end of the column drives the whole screen.
    const dirty = useIsDirty(guard);
    const [saving, setSaving] = useState(false);

    async function save() {
        setSaving(true);
        try {
            await guard?.saveRef.current?.();
        } finally {
            setSaving(false);
        }
    }

    return (
        <StyledCategoryDetail>
            <CategoryTitle
                id={ categoryID }
            />
            <section>
                <h3>Basics</h3>
                <CategoryEditor
                    guard={ guard }
                    id={ categoryID }
                    key={ `editor-${ categoryID }` }
                />
            </section>
            <section>
                <h3>Placement</h3>
                <ParentCategory
                    id={ categoryID }
                    key={ `parent-${ categoryID }` }
                />
            </section>
            <section>
                <h3>Assigned BimClasses</h3>
                <AssignedBimClasses
                    guard={ guard }
                    id={ categoryID }
                    key={ `bimclasses-${ categoryID }` }
                />
            </section>
            <div className="actions">
                <UI.ButtonGroup>
                    <UI.Button
                        disabled={ !dirty || saving }
                        onClick={ () => guard?.resetRef.current?.() }
                        type="button"
                    >
                        Reset
                    </UI.Button>
                    <UI.Button
                        $appearance="primary"
                        disabled={ !dirty || saving }
                        onClick={ save }
                        type="button"
                    >
                        { saving ? "Saving…" : "Save" }
                    </UI.Button>
                </UI.ButtonGroup>
            </div>
        </StyledCategoryDetail>
    );
}
